import styled from '@emotion/styled';
import { BucketId, FileReference } from '@kaizenplatform/belfast-entity';
import { Chip as _Chip, Divider, Typography } from '@material-ui/core';
import { DateTime } from 'luxon';
import { createElement, FC, Fragment } from 'react';
import { FormattedMessage } from 'react-intl';

import {
  CreationSetForProjectFragment,
  CreativeFormat,
  CreativeFormatAspectRatio,
  CreativeFormatFastStart,
  CreativeFormatNetwork,
  CreativeFormatPlacement,
  CreativeFormatPlaybackTime,
  MatchingFormat,
  Option,
} from '../../../graphql/generated/graphql';
import { SingleOfferFragment } from '../../../graphql/generated/graphql_offer';
import { CreationSet } from '../../../service/persistent/CreationSetWebApi';
import _InspectionStatus from '../../component/compound/CreationDetail/InspectionStatus';
import CreationInspectionWorkflow from '../../component/compound/CreationInspectionWorkflow';
import _DeliveryDueDate from '../../component/compound/DeliveryDueDate';
import ExpansionPanel from '../../component/compound/ExpansionPanel';
import _ProjectSummary from '../../component/compound/ProjectSummary';
import LfToBr from '../../component/primitive/LfToBr';
import _Breadcrumbs from '../../connected/Breadcrumbs';
import { SPACING_REGULAR, SPACING_UNIT } from '../../constant/spacing';
import { flexibleLayoutContainer } from '../../styleUtility/flexibleLayout';
import CreationSummary from './CreationSummary';
import _SubmissionContent from './SubmissionContent';

interface Props {
  project: CreationSet['project'];
  assignmentId: string;
  creativeFormat: Pick<CreativeFormat, 'id' | 'resizeBaseId'> & {
    network: Pick<CreativeFormatNetwork, 'key' | 'name'>;
    placement: Pick<CreativeFormatPlacement, 'key' | 'name'>;
    creativeType: Pick<CreativeFormat['creativeType'], 'key' | 'name'>;
    attribute: {
      playbackTime?: {
        seconds: CreativeFormatPlaybackTime['seconds'];
      } | null;
      aspectRatio?: Pick<CreativeFormatAspectRatio, 'aspectRatio' | 'size'> | null;
      fastStart?: CreativeFormatFastStart | null;
    };
    selectedPricingMerchandiseOptions: Array<Pick<Option, 'name' | 'displayToOptimizer' | 'helpUrlForOptimizer'>>;
  };
  assetFiles: ReadonlyArray<FileReference>;
  briefFiles: Map<BucketId, FileReference[]>;
  loading: boolean;
  targetCreationSet: Pick<CreationSetForProjectFragment, 'expressDueDays' | 'creativeFormats'>;
  offer: Pick<SingleOfferFragment, 'expectedDue'>;
  onCreated: (creationId: string) => void;
  language: string;
  className?: string;
}

const CreationContent: FC<Props> = ({
  project,
  assignmentId,
  creativeFormat,
  loading,
  assetFiles,
  briefFiles,
  targetCreationSet,
  offer,
  onCreated,
  language,
  className,
}) => {
  const now = DateTime.local();
  const passedDeadline = !creativeFormat.resizeBaseId && DateTime.fromISO(offer.expectedDue) < now;
  if (loading || !project) {
    return null;
  }

  const resizeBaseCreativeFormat = creativeFormat.resizeBaseId
    ? targetCreationSet.creativeFormats.find((cf) => cf.id == creativeFormat.resizeBaseId)
    : null;
  const resizeCreativeFormats = targetCreationSet.creativeFormats.filter((cf) => cf.resizeBaseId == creativeFormat.id);

  return (
    <Root className={className}>
      <Breadcrumbs />

      <ProjectSummary
        project={project}
        creationState={null}
        offer={offer}
        targetCreationSet={targetCreationSet}
        passedDeadline={passedDeadline}
        creativeCount={0}
        creativeFormat={creativeFormat}
        baseCreativeFormat={resizeBaseCreativeFormat ? resizeBaseCreativeFormat : null}
        resizeCreativeFormats={resizeCreativeFormats}
      />

      <CreationDetails>
        {/*  1. 制作指示書 */}
        <ExpansionPanel
          summary={<FormattedMessage id="show_creation_page.section_heading.creative_brief" />}
          defaultExpanded={true}
        >
          <CreationSummary
            project={project}
            creativeFormat={creativeFormat}
            assetFiles={assetFiles}
            briefFiles={briefFiles}
          />
        </ExpansionPanel>
        {/*  2. 納品フォーム */}
        {passedDeadline && project.matchingFormat == 'competition' ? null : (
          <ExpansionPanel
            summary={
              <SectionSummary>
                <FormattedMessage id="show_creation_page.section_heading.submit_creative" />
                <DeliveryDueDate dueDate={offer.expectedDue} />
                {targetCreationSet.expressDueDays ? (
                  <Chip label={<FormattedMessage id="show_creation_page.express" />} />
                ) : null}
              </SectionSummary>
            }
            defaultExpanded={true}
          >
            <SubmissionDescription>
              {language === 'ja-JP' && <CreationInspectionWorkflow />}
              <Typography>
                <FormattedMessage id="show_creation_page.description.submission_inspection_announce">
                  {(text: string) => <LfToBr text={text} />}
                </FormattedMessage>
              </Typography>
            </SubmissionDescription>
            <SubSectionDivider />
            <SubmissionContent creativeFormat={creativeFormat} assignmentId={assignmentId} onCreated={onCreated} />
            <Fragment>
              <Divider />
              <InspectionStatus state={null} proposed={project.matchingFormat == MatchingFormat.Competition} />
            </Fragment>
          </ExpansionPanel>
        )}
      </CreationDetails>
    </Root>
  );
};

const Root = styled('div')({
  margin: `0 ${SPACING_UNIT}px`,
  padding: `${SPACING_UNIT * 5}px calc((100% - 960px) / 2) ${SPACING_UNIT * 10}px`,
});

const Breadcrumbs = styled(_Breadcrumbs)({ marginBottom: SPACING_UNIT * 5 });

const ProjectSummary = styled(_ProjectSummary)({ marginBottom: SPACING_UNIT * 5 });

const CreationDetails = styled('div')({ marginTop: SPACING_UNIT * 3 });

const SectionSummary = styled('div')(
  flexibleLayoutContainer({
    alignItems: 'center',
  }),
);

const DeliveryDueDate = styled(_DeliveryDueDate)({
  marginLeft: SPACING_REGULAR,
  fontWeight: 550,
  border: '1px solid',
  borderRadius: '15px',
  padding: '4px 12px',
});

const Chip = styled(_Chip)({
  marginLeft: SPACING_REGULAR / 2,
});

const SubSectionDivider = styled(Divider)({ marginLeft: -SPACING_UNIT * 3, marginRight: -SPACING_UNIT * 3 });

const SubmissionDescription = styled('div')({ margin: `0 0 ${SPACING_UNIT * 3}px` });

const SubmissionContent = styled(_SubmissionContent)({ margin: `${SPACING_UNIT * 3}px 0` });

const InspectionStatus = styled(_InspectionStatus)({ margin: `${SPACING_UNIT * 2}px 0 0` });

export default CreationContent;
